import React, { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";

const PHOTOS = [
  {
    src: "/gallery/exterior.jpg",
    tag: "Műhely / Kívülről",
    caption: "Autó-Szerelem — Vadvirág utca 15/D",
    span: "lg:col-span-2 lg:row-span-2",
    aspect: "aspect-[4/3] lg:aspect-auto",
  },
  {
    src: "/gallery/muhely-2.jpg",
    tag: "Szerviztér",
    caption: "Q-Service teljes körű javítás",
    span: "",
    aspect: "aspect-[4/3]",
  },
  {
    src: "/gallery/muhely-1.jpg",
    tag: "Emelő / BMW",
    caption: "Munkában — emelős diagnosztika",
    span: "",
    aspect: "aspect-[4/3]",
  },
  {
    src: "/gallery/muhely-3-trailer.jpg",
    tag: "Assistance",
    caption: "BMW E92 trailer-en — érted megyünk",
    span: "",
    aspect: "aspect-[4/3]",
  },
];

export default function Gallery() {
  const [openIdx, setOpenIdx] = useState(null);

  const close = useCallback(() => setOpenIdx(null), []);

  useEffect(() => {
    if (openIdx === null) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [openIdx, close]);

  return (
    <section
      id="galeria"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="section-eyebrow mb-4">02 / Galéria</span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
            >
              A műhely
              <br />
              <span className="text-[#0082D2]">belülről.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">
            Saját műhelyünk, saját csapatunk — Debrecen szívében.
            Modern emelők, márkafüggetlen diagnosztika, BMW szakrészleg.
          </p>
        </div>

        <div
          className="grid grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-3"
          data-testid="gallery-grid"
        >
          {PHOTOS.map((p, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setOpenIdx(idx)}
              className={`relative ${p.aspect} ${p.span} bg-[#101010] border border-white/5 overflow-hidden group transition-all hover:border-[#0082D2]/60 cursor-zoom-in col-span-2 lg:col-span-2`}
              data-testid={`gallery-photo-${idx}`}
              aria-label={`${p.tag} — ${p.caption}`}
            >
              <img
                src={p.src}
                alt={`${p.tag} — ${p.caption}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* dark overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              {/* caption */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 text-left">
                <span
                  className="block text-[0.62rem] sm:text-[0.7rem] font-bold uppercase tracking-[0.28em] text-[#0082D2]"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {p.tag}
                </span>
                <span className="block mt-1 text-sm sm:text-base text-white font-semibold">
                  {p.caption}
                </span>
              </div>
              {/* M-stripe accent */}
              <div className="absolute top-0 left-0 right-0 m-stripe h-[2px] opacity-40 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-zinc-600 uppercase tracking-[0.2em]">
          Kattints egy képre a nagyításhoz
        </p>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Bezárás"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={PHOTOS[openIdx].src}
            alt={PHOTOS[openIdx].caption}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="absolute bottom-6 left-0 right-0 text-center pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className="inline-block text-[0.7rem] font-bold uppercase tracking-[0.28em] text-[#0082D2]"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              {PHOTOS[openIdx].tag}
            </span>
            <p className="mt-1 text-white/90">{PHOTOS[openIdx].caption}</p>
          </div>
        </div>
      )}
    </section>
  );
}
